import { Navigate, Route, Routes } from 'react-router-dom';
import { useAuth } from './lib/auth';
import { Login } from './pages/Login';
import { SignInComplete } from './pages/SignInComplete';
import { FishersLicensing } from './pages/fisheries/FishersLicensing';
import { CatchQuota } from './pages/fisheries/CatchQuota';
import { ExportCertificates } from './pages/fisheries/ExportCertificates';
import { AquaculturePermits } from './pages/fisheries/AquaculturePermits';
import { Fiti } from './pages/fisheries/Fiti';
import { FarmersHoldings } from './pages/agriculture/FarmersHoldings';
import { Subsidies } from './pages/agriculture/Subsidies';
import { AgriLand } from './pages/agriculture/AgriLand';
import { Biosecurity } from './pages/agriculture/Biosecurity';
import { Pesticides } from './pages/agriculture/Pesticides';
import { Livestock } from './pages/agriculture/Livestock';
import { AbattoirVet } from './pages/agriculture/AbattoirVet';
import { BlueGrants } from './pages/blueeconomy/BlueGrants';
import { MarineConcessions } from './pages/blueeconomy/MarineConcessions';
import { ResearchPermits } from './pages/blueeconomy/ResearchPermits';
import { MspViewer } from './pages/blueeconomy/MspViewer';
import { Shell, type NavHeader, type NavItem } from './ui/Shell';

const NAV: Array<NavItem | NavHeader> = [
  { header: 'Fisheries — M32 (SFA)' },
  { to: '/fisheries/fishers-licensing', label: 'Fishers & Licensing', icon: '⚓' },
  { to: '/fisheries/catch-quota', label: 'Catch Reports & Quota', icon: '🐟' },
  { to: '/fisheries/export-certificates', label: 'Export Certificates', icon: '📦' },
  { to: '/fisheries/aquaculture-permits', label: 'Aquaculture Permits', icon: '🦐' },
  { to: '/fisheries/fiti', label: 'FiTI Publications', icon: '📊' },

  { header: 'Agriculture & Bio-security — M33 (SAA)' },
  { to: '/agriculture/farmers-holdings', label: 'Farmers & Holdings', icon: '🌾' },
  { to: '/agriculture/subsidies', label: 'Subsidy Schemes & Claims', icon: '💰' },
  { to: '/agriculture/agri-land', label: 'Agri-Land Applications', icon: '🗺' },
  { to: '/agriculture/biosecurity', label: 'Bio-Security & Quarantine', icon: '🛡', exact: false },
  { to: '/agriculture/pesticides', label: 'Pesticide Registration', icon: '🧪' },
  { to: '/agriculture/livestock', label: 'Livestock & Outbreaks', icon: '🐄', exact: false },
  { to: '/agriculture/abattoir-vet', label: 'Abattoirs & Vet Bookings', icon: '🩺' },

  { header: 'Blue Economy Projects — M34' },
  { to: '/blue-economy/grants', label: 'Blue Grants', icon: '🌊' },
  { to: '/blue-economy/marine-concessions', label: 'Marine Concessions', icon: '⛴' },
  { to: '/blue-economy/research-permits', label: 'Research Permits', icon: '🔬' },
  { to: '/blue-economy/msp-viewer', label: 'MSP Zone Viewer', icon: '🧭' },
];

const ROLE_TO_LANDING: Record<string, string> = {
  SFA_LICENSING: '/fisheries/fishers-licensing',
  QUOTA_MANAGER: '/fisheries/catch-quota',
  EXPORT_CERT_OFFICER: '/fisheries/export-certificates',
  OBSERVER_COORDINATOR: '/fisheries/catch-quota',
  AGRI_OFFICER: '/agriculture/farmers-holdings',
  BIOSECURITY_OFFICER: '/agriculture/biosecurity',
  VET: '/agriculture/livestock',
  SCHEME_ADMIN: '/agriculture/subsidies',
  BLUE_ECON_OFFICER: '/blue-economy/marine-concessions',
  MSP_ADMIN: '/blue-economy/msp-viewer',
  GRANTS_PANEL: '/blue-economy/grants',
};

export function App() {
  const { persona } = useAuth();

  if (!persona) {
    return (
      <Routes>
        <Route path="/signin/complete" element={<SignInComplete />} />
        <Route path="*" element={<Login />} />
      </Routes>
    );
  }

  // Land each persona somewhere useful for their primary duty rather than
  // always defaulting to the first workspace in the sidebar.
  const landing = persona.roles.map((r) => ROLE_TO_LANDING[r]).find(Boolean) ?? '/fisheries/fishers-licensing';

  return (
    <Routes>
      <Route element={<Shell title={persona.org} items={NAV} />}>
        <Route index element={<Navigate to={landing} replace />} />

        <Route path="fisheries/fishers-licensing" element={<FishersLicensing />} />
        <Route path="fisheries/catch-quota" element={<CatchQuota />} />
        <Route path="fisheries/export-certificates" element={<ExportCertificates />} />
        <Route path="fisheries/aquaculture-permits" element={<AquaculturePermits />} />
        <Route path="fisheries/fiti" element={<Fiti />} />

        <Route path="agriculture/farmers-holdings" element={<FarmersHoldings />} />
        <Route path="agriculture/subsidies" element={<Subsidies />} />
        <Route path="agriculture/agri-land" element={<AgriLand />} />
        <Route path="agriculture/biosecurity" element={<Biosecurity />} />
        <Route path="agriculture/pesticides" element={<Pesticides />} />
        <Route path="agriculture/livestock" element={<Livestock />} />
        <Route path="agriculture/abattoir-vet" element={<AbattoirVet />} />

        <Route path="blue-economy/grants" element={<BlueGrants />} />
        <Route path="blue-economy/marine-concessions" element={<MarineConcessions />} />
        <Route path="blue-economy/research-permits" element={<ResearchPermits />} />
        <Route path="blue-economy/msp-viewer" element={<MspViewer />} />
      </Route>

      <Route path="*" element={<Navigate to={landing} replace />} />
    </Routes>
  );
}
